import { Elements, CardExpiryElement, CardCvcElement, useElements, CardNumberElement } from '@stripe/react-stripe-js';
import { CheckoutContext } from '@/context/CheckoutContext';
import { useCallback, useContext, useEffect, useRef, useState, type RefObject } from 'react';
import type {
  StripeCardNumberElement,
  StripeCardExpiryElement,
  StripeElementLocale,
  StripeCardCvcElement,
  StripeError,
} from '@stripe/stripe-js';
import { inputStyles } from '@/styles/input';
import { cn } from '@/utils/cn';
import { CardBrandIcons } from '../PaymentInputs/CardBrandIcons';
import { FontAwesomeIcon } from '@/components/Common/FontAwesomeIcon/FontAwesomeIcon';
import { InputTaxComponent } from '../InputTaxComponent/InputTaxComponent';
import { Email } from '../Common/Email';
import { shouldHideEmail } from '@/utils/conditionalRendering';
import { getLocale } from '@/utils/get-locale-util';
import { STRIPE_APPEARANCE } from '@/constants/stripe';
import { useTranslate } from '@/hooks/useTranslate';
import { getStripeErrorMessage } from '@/utils/stripe';
import { PaymentMethodEnum } from '@/types/common';
import { useStripeInitialize } from '@/hooks/useStripeInitialize';
import { isEmailValid } from '@/utils/email';
import isSafari from '@/utils/safari';
import { trackCardFieldInvalid, trackCardFormStarted } from '@/utils/conversionTracking';
import PaymentMethodNotSupported from '@/components/PaymentMethod/PaymentMethodNotSupported';
import { Loader } from '../Common/Loader';
import { logError } from '@/utils/logger';
import { ErrorLogs } from '@/constants/logs';

interface NewCardFormProps {
  cardElementRef: RefObject<StripeCardNumberElement | null>;
}

// Stripe card fields render inside a cross-origin js.stripe.com iframe, so our
// CSS can't reach them — the font-size must be set here via `style.base`. Use
// 16px to match every other checkout input; >=16px also prevents iOS Safari's
// focus-zoom (it zooms any focused field < 16px, ignoring user-scalable).
//
// Stripe Card Elements do not reliably support padding in `style.base`.
// Keep the line box slightly smaller than our 44px (`h-11`) wrapper so
// Stripe's placeholder/text sits visually centered instead of a bit low.
const STRIPE_FIELD_STYLE = { base: { fontSize: '16px', lineHeight: '40px' } } as const;

// Card data (number/expiry/cvc) is inherently LTR. Stripe sets each field's
// direction AND caret position from the Elements `locale`, so under an RTL
// locale the value and caret flip right and the digits hide behind the
// right-pinned card-brand logos. `style.base` has no `direction` property to
// override this, so fall back to LTR `en` for these fields when the store
// locale is RTL. Only the cosmetic placeholders (MM/YY, CVC) change language —
// standard for card inputs. `ar` and `he` are the only RTL entries in Stripe's
// `StripeElementLocale` union (see @stripe/stripe-js elements-group.d.ts).
const RTL_LANGUAGES = ['ar', 'he'];
const getCardFieldLocale = (locale: string): string => (RTL_LANGUAGES.includes(locale) ? 'en' : locale);

const NewCardFormContent = ({ cardElementRef }: NewCardFormProps) => {
  const elements = useElements();
  const context = useContext(CheckoutContext);
  const {
    customer,
    featureFlags,
    template,
    actions: { setIsFormValid },
    card: { setCardElementRef },
  } = context;
  const { t } = useTranslate();
  const [cardNumberValid, setCardNumberValid] = useState(false);
  const [cardExpiryValid, setCardExpiryValid] = useState(false);
  const [cardCvcValid, setCardCvcValid] = useState(false);
  const [cardNumberError, setCardNumberError] = useState<string | null>(null);
  const [cardExpiryError, setCardExpiryError] = useState<string | null>(null);
  const [cardCvcError, setCardCvcError] = useState<string | null>(null);
  const [isZipCodeValid, setIsZipCodeValid] = useState(false);
  // A field that reports `loaderror` never paints its iframe, so `loader: 'auto'`
  // would keep its skeleton up indefinitely.
  const [hasLoadError, setHasLoadError] = useState(false);
  const activeFieldRef = useRef<string | null>(null);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const expiryElementRef = useRef<StripeCardExpiryElement | null>(null);
  const cvcElementRef = useRef<StripeCardCvcElement | null>(null);

  useEffect(() => {
    if (!elements || !cardElementRef) return;

    const cardElement = elements.getElement(CardNumberElement);
    if (cardElement) {
      (cardElementRef as React.MutableRefObject<StripeCardNumberElement | null>).current = cardElement;
      setCardElementRef(cardElementRef);
    }
  }, [elements, cardElementRef, setCardElementRef]);

  useEffect(() => {
    const isFormValid =
      cardNumberValid &&
      cardExpiryValid &&
      cardCvcValid &&
      isZipCodeValid &&
      isEmailValid(customer?.email) &&
      !cardNumberError &&
      !cardExpiryError &&
      !cardCvcError;
    setIsFormValid(isFormValid);
  }, [
    cardNumberValid,
    cardExpiryValid,
    cardCvcValid,
    isZipCodeValid,
    cardNumberError,
    cardExpiryError,
    cardCvcError,
    customer?.email,
    setIsFormValid,
  ]);

  const onTaxValidationChange = useCallback((isValid: boolean) => {
    setIsZipCodeValid(isValid);
  }, []);

  // Only the card number carries `onLoadError` in Stripe's types, and a failure
  // there takes the whole form down anyway — expiry and CVC are unusable on
  // their own.
  const handleCardNumberLoadError = useCallback((event: { error?: StripeError }) => {
    logError(ErrorLogs.stripeCardFormError, event?.error);
    setHasLoadError(true);
  }, []);

  if (hasLoadError) {
    return <PaymentMethodNotSupported />;
  }

  return (
    <div className="flex flex-col items-center w-full gap-3">
      <div className="flex flex-col gap-1 w-full">
        <div className="relative w-full">
          <div
            className={cn(inputStyles, 'w-full p-0 pl-2', cardNumberError && 'border-red-500')}
            data-testid="stripe-card-number-wrapper"
          >
            <CardNumberElement
              options={{
                iconStyle: 'default',
                disableLink: true,
                style: STRIPE_FIELD_STYLE,
              }}
              onChange={(e) => {
                setCardNumberValid(e.complete);
                setCardNumberError(getStripeErrorMessage(t, e.error || null, 'number'));
                if (e.error) trackCardFieldInvalid(context, 'number');
                if (e.complete) {
                  if (isSafari()) {
                    expiryElementRef.current?.focus();
                  } else {
                    setTimeout(() => expiryElementRef.current?.focus(), 0);
                  }
                }
              }}
              onFocus={() => {
                trackCardFormStarted(context);
                if (blurTimeoutRef.current) {
                  clearTimeout(blurTimeoutRef.current);
                  blurTimeoutRef.current = null;
                }
                activeFieldRef.current = 'number';
              }}
              onBlur={() => {
                // Delay clearing active field to allow focus transition to next field
                blurTimeoutRef.current = setTimeout(() => {
                  if (activeFieldRef.current === 'number') {
                    activeFieldRef.current = null;
                  }
                }, 100);
              }}
              onLoadError={handleCardNumberLoadError}
            />
          </div>
          <CardBrandIcons />
        </div>
        {cardNumberError && <div className="text-xs text-red-500">{cardNumberError}</div>}
      </div>
      <div className="flex items-start w-full gap-4">
        <div className="flex flex-col gap-1 flex-1">
          <div
            className={cn(inputStyles, 'w-full p-0 pl-2', cardExpiryError && 'border-red-500')}
            data-testid="stripe-card-expiry-wrapper"
          >
            <CardExpiryElement
              options={{ style: STRIPE_FIELD_STYLE }}
              onReady={(el) => {
                expiryElementRef.current = el;
              }}
              onChange={(e) => {
                setCardExpiryValid(e.complete);
                setCardExpiryError(getStripeErrorMessage(t, e.error || null, 'expiry'));
                if (e.error) trackCardFieldInvalid(context, 'expiry');
                if (e.complete) {
                  setTimeout(() => cvcElementRef.current?.focus(), 0);
                }
              }}
              onFocus={() => {
                trackCardFormStarted(context);
                if (blurTimeoutRef.current) {
                  clearTimeout(blurTimeoutRef.current);
                  blurTimeoutRef.current = null;
                }
                activeFieldRef.current = 'expiry';
              }}
              onBlur={() => {
                // Delay clearing active field to allow focus transition to next field
                blurTimeoutRef.current = setTimeout(() => {
                  if (activeFieldRef.current === 'expiry') {
                    activeFieldRef.current = null;
                  }
                }, 100);
              }}
            />
          </div>
          {cardExpiryError && <div className="text-xs text-red-500">{cardExpiryError}</div>}
        </div>
        <div className="flex flex-col gap-1 flex-1">
          <div className="relative w-full">
            <div
              className={cn(inputStyles, 'w-full p-0 pl-2', cardCvcError && 'border-red-500')}
              data-testid="stripe-card-cvc-wrapper"
            >
              <CardCvcElement
                options={{ style: STRIPE_FIELD_STYLE }}
                onChange={(e) => {
                  setCardCvcValid(e.complete);
                  setCardCvcError(getStripeErrorMessage(t, e.error || null, 'cvc'));
                  if (e.error) trackCardFieldInvalid(context, 'cvc');
                }}
                onReady={(el) => {
                  cvcElementRef.current = el;
                }}
                onFocus={() => {
                  trackCardFormStarted(context);
                  if (blurTimeoutRef.current) {
                    clearTimeout(blurTimeoutRef.current);
                    blurTimeoutRef.current = null;
                  }
                  activeFieldRef.current = 'cvc';
                }}
                onBlur={() => {
                  // Delay clearing active field to allow focus transition to next field
                  blurTimeoutRef.current = setTimeout(() => {
                    if (activeFieldRef.current === 'cvc') {
                      activeFieldRef.current = null;
                    }
                  }, 100);
                }}
              />
            </div>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <FontAwesomeIcon icon="fa-light fa-credit-card" className="text-muted-foreground text-lg" />
            </div>
          </div>
          {cardCvcError && <div className="text-xs text-red-500">{cardCvcError}</div>}
        </div>
      </div>
      <InputTaxComponent onValidate={onTaxValidationChange} />
      {!shouldHideEmail(featureFlags, customer, template.features) && (
        <div className="w-full">
          <Email />
        </div>
      )}
    </div>
  );
};

export const StripeCardForm = () => {
  const {
    stripe,
    isLoading: isStripeLoading,
    stripePromise,
  } = useStripeInitialize({ paymentMethod: PaymentMethodEnum.CREDIT_CARD });
  const cardElementRef = useRef<StripeCardNumberElement | null>(null);
  const { pspLocale } = getLocale();

  if (isStripeLoading) {
    return <Loader />;
  }

  /*
   * No publishable key configured for this tenant (`stripePromise === null`),
   * or Stripe.js settled without an instance after every reload attempt
   * (`!stripe` once loading is done). Neither can ever mount a card field, and
   * `<Elements>` has no timeout of its own, so the form would sit on
   * Stripe's skeleton forever. Surface it as unavailable instead.
   */
  if (!stripePromise || !stripe) {
    return <PaymentMethodNotSupported />;
  }

  return (
    <Elements
      /*
       * Pass the Promise directly (officially supported by
       * `@stripe/react-stripe-js`) so the form LAYOUT (email,
       * bordered card-number wrapper, expiry, CVC, ZIP) mounts on
       * the very first paint. Stripe Elements awaits the promise
       * internally and only delays the iframe mounts — combined
       * with `loader: 'auto'` below, each wrapper shows a themed
       * skeleton in-place from frame 1. This replaces the prior
       * "tiny centered spinner → full form layout" hard swap (the
       * ~200ms re-render that produced a visible flicker) with one
       * smooth in-wrapper skeleton→iframe transition.
       */
      stripe={stripePromise}
      /*
       * `loader: 'auto'` — Stripe renders its own themed skeleton
       * placeholder inside each Element's wrapper while the iframe
       * mounts (typically 150-400ms cold), instead of leaving the
       * bordered <div> visibly empty. The skeleton inherits
       * `STRIPE_APPEARANCE` (notably borderRadius: 6px) so it
       * blends with our `inputStyles` wrapper. Wallet flows keep
       * `loader: 'never'` because they already render their own
       * `WalletButtonShell` placeholder — stacking two skeletons
       * would regress that UX. See `git log -S "loader: 'never'"`
       * for the original (undocumented) introduction in PR #12.
       */
      options={{
        locale: getCardFieldLocale(pspLocale) as StripeElementLocale,
        appearance: STRIPE_APPEARANCE,
        loader: 'auto',
      }}
    >
      <NewCardFormContent cardElementRef={cardElementRef as RefObject<StripeCardNumberElement | null>} />
    </Elements>
  );
};
